/**
 * ToolUsageTracker - Batches tool usage events and flushes to analytics endpoint.
 * Uses sendBeacon for reliable delivery during page unload.
 */

export interface ToolEvent {
  toolId: string;
  action: 'activate' | 'deactivate' | 'setting_change';
  ts: number;
  durationMs?: number;
  setting?: string;
  value?: string | number;
}

export interface ToolUsageTrackerConfig {
  apiEndpoint: string;
  authHeaders: Record<string, string>;
  sessionId: string;
  /** Page URL to report (default: location.href). LMS presets strip query/hash. */
  pageUrl?: () => string;
  flushIntervalMs?: number;
  maxBufferSize?: number;
}

export class ToolUsageTracker {
  private buffer: ToolEvent[] = [];
  private activationTimes: Map<string, number> = new Map();
  private settingDebounceTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private config: Required<Omit<ToolUsageTrackerConfig, 'pageUrl'>> & Pick<ToolUsageTrackerConfig, 'pageUrl'>;
  private boundBeforeUnload: () => void;

  private isLoopback: boolean = false;

  constructor(config: ToolUsageTrackerConfig) {
    this.config = {
      flushIntervalMs: 30000,
      maxBufferSize: 50,
      ...config,
    };

    // Detect loopback endpoints — skip network calls to avoid PNA/CORS errors in extensions
    try {
      const host = new URL(config.apiEndpoint).hostname.toLowerCase();
      this.isLoopback = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
    } catch { /* not a full URL, likely relative path — fine */ }

    this.boundBeforeUnload = () => this.flush(true);
    window.addEventListener('beforeunload', this.boundBeforeUnload);

    this.flushTimer = setInterval(() => this.flush(false), this.config.flushIntervalMs);
  }

  trackActivate(toolId: string): void {
    this.activationTimes.set(toolId, Date.now());
    this.buffer.push({ toolId, action: 'activate', ts: Date.now() });
    this.maybeFlush();
  }

  trackDeactivate(toolId: string): void {
    const startTime = this.activationTimes.get(toolId);
    const durationMs = startTime ? Date.now() - startTime : undefined;
    this.activationTimes.delete(toolId);
    this.buffer.push({ toolId, action: 'deactivate', ts: Date.now(), durationMs });
    this.maybeFlush();
  }

  trackSettingChange(toolId: string, setting: string, value: string | number): void {
    const key = `${toolId}:${setting}`;
    const existing = this.settingDebounceTimers.get(key);
    if (existing) clearTimeout(existing);

    this.settingDebounceTimers.set(key, setTimeout(() => {
      this.settingDebounceTimers.delete(key);
      this.buffer.push({ toolId, action: 'setting_change', ts: Date.now(), setting, value });
      this.maybeFlush();
    }, 500));
  }

  private maybeFlush(): void {
    if (this.buffer.length >= this.config.maxBufferSize) {
      this.flush(false);
    }
  }

  flush(useBeacon: boolean): void {
    if (this.buffer.length === 0) return;
    if (this.isLoopback) { this.buffer.length = 0; return; }

    const events = this.buffer.splice(0);
    const payload = JSON.stringify({
      action: 'TOOL_USAGE',
      sessionId: this.config.sessionId,
      url: this.config.pageUrl ? this.config.pageUrl() : window.location.href,
      timestamp: Date.now(),
      data: { tools: events },
      ...this.authBodyFields(),
    });

    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(`${this.config.apiEndpoint}/analytics`, blob);
    } else {
      fetch(`${this.config.apiEndpoint}/analytics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.config.authHeaders,
        },
        body: payload,
        keepalive: true,
      }).catch(() => { /* analytics should not break the widget */ });
    }
  }

  private authBodyFields(): Record<string, string> {
    const fields: Record<string, string> = {};
    if (this.config.authHeaders['X-API-Key']) {
      fields.apiKey = this.config.authHeaders['X-API-Key'];
    }
    if (this.config.authHeaders['X-License-Key']) {
      fields.licenseKey = this.config.authHeaders['X-License-Key'];
    }
    return fields;
  }

  destroy(): void {
    this.flush(true);
    window.removeEventListener('beforeunload', this.boundBeforeUnload);
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.settingDebounceTimers.forEach(t => clearTimeout(t));
    this.settingDebounceTimers.clear();
  }
}
